import { useState, useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Input } from '@/components/ui/input'
import { Combobox } from '@/components/ui/combobox'
import { deriveAccentRamp, contrastRatio, FONT_PAIRINGS } from '@/lib/theme'
import { useTenantBranding, useUpdateBranding } from '@/hooks/use-branding'
import { supabase } from '@/lib/supabase'
import { validateLogoFile } from '@/lib/logo-validation'
import { buildLogoUrl } from '@/lib/logo-url'

export const Route = createFileRoute('/_authed/settings/branding')({
  component: BrandingPage,
})

function FolioRule({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3">
      <div className="h-px flex-1 bg-[color:var(--folio-rule)]" />
      <span className="text-[11px] font-[550] uppercase tracking-[0.04em] text-[color:var(--ink-3)]">
        {label}
      </span>
      <div className="h-px flex-1 bg-[color:var(--folio-rule)]" />
    </div>
  )
}

const HEX_RE = /^#[0-9a-fA-F]{6}$/

function AccentColorSection() {
  const { data: branding, isLoading } = useTenantBranding()
  const updateBranding = useUpdateBranding()

  const [hex, setHex] = useState('')
  const [error, setError] = useState<string | null>(null)

  // Populate from saved branding once loaded (and re-sync after a save)
  const [prevAccent, setPrevAccent] = useState<string | undefined>(undefined)
  if (branding?.accent !== prevAccent) {
    setPrevAccent(branding?.accent)
    if (branding?.accent) setHex(branding.accent)
  }

  const isValid = HEX_RE.test(hex)
  const previewRamp = isValid ? deriveAccentRamp(hex, false) : null

  // Warn if the derived accent/fg contrast is below AA (can happen in extreme edge cases)
  const contrastWarn = previewRamp
    ? contrastRatio(previewRamp.accent, previewRamp.accentFg) < 4.5
    : false

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value
    setHex(val)
    if (error) setError(null)
  }

  function handleBlur() {
    if (!hex || hex === branding?.accent) return
    if (!isValid) {
      setError('Enter a valid 6-digit hex color (e.g. #3B82F6)')
      return
    }
    if (contrastWarn) {
      setError('This color cannot achieve sufficient contrast — try a mid-range hue.')
      return
    }
    setError(null)
    const ramp = deriveAccentRamp(hex, false)
    updateBranding.mutate({ accent: hex, accentRamp: ramp })
  }

  return (
    <section className="space-y-4">
      <FolioRule label="Accent Color" />
      <p className="text-[13px] leading-5 text-[color:var(--ink-3)]">
        Set the brand accent used on buttons, links, and active states. Enter a hex value —
        the color is automatically adjusted to meet WCAG AA contrast in both light and dark modes.
      </p>
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div
            className="h-8 w-8 shrink-0 rounded-[6px] border border-[color:var(--line)] transition-colors"
            style={{ backgroundColor: previewRamp?.accent ?? 'var(--accent-color)' }}
            aria-hidden="true"
          />
          <Input
            type="text"
            value={hex}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="#3B82F6"
            maxLength={7}
            disabled={isLoading || updateBranding.isPending}
            className="w-36 font-mono text-[13px]"
            aria-label="Accent color hex"
          />
          {updateBranding.isPending && (
            <span className="text-[12px] text-[color:var(--ink-3)]">Saving…</span>
          )}
        </div>
        {error && (
          <p className="text-[12px] text-destructive">{error}</p>
        )}
        {previewRamp && !error && (
          <p className="text-[12px] text-[color:var(--ink-3)]">
            Derived accent: {previewRamp.accent} · fg: {previewRamp.accentFg}
          </p>
        )}
      </div>
    </section>
  )
}

function LogoSection() {
  const { data: branding, isLoading } = useTenantBranding()
  const updateBranding = useUpdateBranding()
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    // Reset the input so the same file can trigger onChange again after an error
    if (fileRef.current) fileRef.current.value = ''
    if (!file) return

    const validation = await validateLogoFile(file)
    if (!validation.ok) {
      setError(validation.error)
      return
    }

    setError(null)
    setUploading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Not authenticated')

      const body = new FormData()
      body.append('file', file)

      const res = await fetch('/api/logo-upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body,
      })

      if (!res.ok) {
        const payload = await res.json() as { error?: string }
        throw new Error(payload.error ?? 'Upload failed')
      }

      const { key } = await res.json() as { key: string }
      updateBranding.mutate({ logo: key })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  function handleRemove() {
    updateBranding.mutate({ logo: null })
  }

  const currentKey = branding?.logo
  const logoUrl = currentKey ? buildLogoUrl(currentKey) : null
  const busy = isLoading || uploading || updateBranding.isPending

  return (
    <section className="space-y-4">
      <FolioRule label="Logo" />
      <p className="text-[13px] leading-5 text-[color:var(--ink-3)]">
        Upload a logo shown in the sidebar and branding preview.
        Accepted formats: PNG or JPEG · max 2 MB.
      </p>

      {logoUrl ? (
        <div className="space-y-3">
          <img
            src={logoUrl}
            alt="Tenant logo"
            className="h-12 w-auto rounded-[6px] border border-[color:var(--line)] object-contain p-1"
          />
          <div className="flex items-center gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className="text-[12px] text-[color:var(--ink-3)] underline underline-offset-2 hover:text-[color:var(--ink)] disabled:opacity-40"
            >
              Replace
            </button>
            <span className="select-none text-[12px] text-[color:var(--line)]">·</span>
            <button
              type="button"
              disabled={busy}
              onClick={handleRemove}
              className="text-[12px] text-destructive underline underline-offset-2 hover:opacity-80 disabled:opacity-40"
            >
              Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="inline-flex h-8 items-center gap-2 rounded-[6px] border border-[color:var(--line)] bg-[color:var(--bg)] px-3 text-[12px] font-[500] text-[color:var(--ink)] hover:bg-[color:var(--bg-2)] disabled:opacity-40"
        >
          {uploading ? 'Uploading…' : 'Choose file'}
        </button>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg"
        className="hidden"
        onChange={handleFile}
      />

      {error && <p className="text-[12px] text-destructive">{error}</p>}
    </section>
  )
}

const FONT_PAIRING_OPTIONS = FONT_PAIRINGS.map((p) => ({ value: p.key, label: p.label }))

function FontPairingSection() {
  const { data: branding, isLoading } = useTenantBranding()
  const updateBranding = useUpdateBranding()

  const currentKey = branding?.font_pairing ?? 'folio'
  const currentPairing = FONT_PAIRINGS.find((p) => p.key === currentKey) ?? FONT_PAIRINGS[0]

  function handleSelect(value: string | undefined) {
    if (!value || value === currentKey) return
    updateBranding.mutate({ font_pairing: value })
  }

  return (
    <section className="space-y-4">
      <FolioRule label="Typography" />
      <p className="text-[13px] leading-5 text-[color:var(--ink-3)]">
        Choose a font pairing for the interface. Selection is limited to curated pairings
        that meet the product's density and readability standards.
      </p>
      <div className="space-y-2">
        <Combobox
          options={FONT_PAIRING_OPTIONS}
          value={currentKey}
          onChange={handleSelect}
          disabled={isLoading || updateBranding.isPending}
          placeholder="Select pairing…"
          searchPlaceholder="Search pairings…"
          className="max-w-[280px]"
        />
        {updateBranding.isPending && (
          <p className="text-[12px] text-[color:var(--ink-3)]">Saving…</p>
        )}
        {!isLoading && (
          <p className="text-[12px] text-[color:var(--ink-3)]">{currentPairing.description}</p>
        )}
      </div>
    </section>
  )
}

function BrandingPage() {
  return (
    <div className="flex h-full flex-col bg-[color:var(--bg)]">
      {/* Slice 1B-1: "Settings" is titled by the ONE shared header; no
          route-owned 48px bar. */}
      <div className="flex-1 overflow-auto p-6">
        <div className="max-w-[720px] space-y-10">
          <AccentColorSection />
          <LogoSection />
          <FontPairingSection />
        </div>
      </div>
    </div>
  )
}
